/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'wouter';

import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useUrlGenerator } from '@micromag/core/contexts';
import Viewer from '@micromag/viewer';

import { useApp } from '../../../contexts/AppContext';
import { useOrganisation } from '../../../contexts/OrganisationContext';
import useStory from '../../../hooks/useStory';
import useStoryTrackingVariables from '../../../hooks/useStoryTrackingVariables';
import useStoryVersion from '../../../hooks/useStoryVersion';
import MainLayout from '../../layouts/Main';
import StoryVersionMenubar from '../../menubars/StoryVersion';
import PreviewNavbar from '../../navbars/Preview';
import Page from '../../partials/Page';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const StoryPreviewPage = ({ className }) => {
    const [pathname] = useLocation();
    const url = useUrlGenerator();

    const { memoryRouter } = useApp();
    const { story: storyId = null, version: versionId = null } = useParams();
    const organisation = useOrganisation();
    const { branding = {} } = organisation || {};

    const { story: latestVersionStory = null } = useStory(storyId, { withoutCache: true });
    const { last_version: lastVersion = null } = latestVersionStory || {};
    const { id: lastVersionId = null } = lastVersion || {};

    const version = useStoryVersion(storyId, versionId, {
        autoload: storyId !== null && versionId !== null,
    });
    const isCurrentVersion = versionId === lastVersionId;

    const { components: versionScreens = null } = version || {};
    const hasVersion = version !== null;
    const isVersionView = versionId !== null;

    const story =
        isVersionView && hasVersion
            ? { ...latestVersionStory, components: versionScreens }
            : latestVersionStory;
    const hasStory = story !== null;

    const storyDefaultTitle = (
        <FormattedMessage defaultMessage="Screens" descrition="Screens title" />
    );
    const versionsTitle = (
        <FormattedMessage defaultMessage="Versions" description="Versions title" />
    );
    const versionTitle = (
        <FormattedMessage defaultMessage="Consult a version" descrition="Consult a version title" />
    );
    const previewVersionTitle = (
        <FormattedMessage defaultMessage="Preview a version" descrition="Preview a version title" />
    );

    const storyTitle = hasStory ? story.title : storyDefaultTitle;
    const trackingVariables = useStoryTrackingVariables(story, organisation);

    const mainLayoutProps =
        isVersionView && hasStory
            ? {
                  nav: [
                      {
                          label: storyTitle,
                          url: url('stories.show', { story: storyId }),
                      },
                      {
                          label: versionsTitle,
                          url: url('stories.versions.index', { story: storyId }),
                      },
                      {
                          label: versionTitle,
                          url: url('stories.versions.show', { story: storyId, version: versionId }),
                      },
                      {
                          label: previewVersionTitle,
                          url: pathname,
                      },
                  ],
              }
            : { navbar: <PreviewNavbar story={story} /> };

    // console.log('hum', story);

    console.log(isVersionView
        ? url('stories.versions.preview', {
              story: storyId,
              version: versionId,
          })
        : url('stories.preview', {
              story: storyId,
          }), memoryRouter);

    return (
        <MainLayout fullscreen {...mainLayoutProps}>
            <div
                className={classNames([
                    'd-flex',
                    'flex-column',
                    'position-relative',
                    'h-100',
                    className,
                ])}
            >
                {isVersionView && hasStory && hasVersion ? (
                    <Page
                        menubar={
                            <StoryVersionMenubar
                                story={story}
                                version={version}
                                current={isCurrentVersion}
                            />
                        }
                    />
                ) : null}
                {hasStory && (!isVersionView || hasVersion) ? (
                    <div className={classNames(['position-relative', 'flex-grow-1'])}>
                        <Viewer
                            story={story}
                            theme={branding}
                            memoryRouter={memoryRouter}
                            basePath={
                                isVersionView
                                    ? url('stories.versions.preview', {
                                          story: storyId,
                                          version: versionId,
                                      })
                                    : url('stories.preview', {
                                          story: storyId,
                                      })
                            }
                            fullscreen
                            trackingVariables={trackingVariables}
                            withMetadata
                        />
                    </div>
                ) : null}
            </div>
        </MainLayout>
    );
};

StoryPreviewPage.defaultProps = defaultProps;
StoryPreviewPage.propTypes = propTypes;

export default StoryPreviewPage;
